import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Download, Plus, Loader2, X, Search, Instagram, Music, UserSearch } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { adminApi } from "@/lib/api";
import { selectCls } from "./shared";

const CH_RE = /sviz|swit|ticino|lugan|zur|zür|gine|genev|bern|basil|lucern|locarno|bellinzona|losanna|lausanne|paradiso|massagno|mendrisio|chiasso/i;
const SRC = { openstreetmap: "OSM", google_places: "Google", apify_instagram: "Apify IG", apify_tiktok: "Apify TT" };

const ScrapeReport = ({ r }) => (
  <div data-testid="scrape-report" className="px-6 py-3 border-b border-[#d5dbde] text-[11px] font-mono text-[#5b6168] space-y-1">
    {r.place && <div>Zona: <span className="text-[#2B3036]">{r.place}</span> · raggio {r.radius_km} km</div>}
    <div>OpenStreetMap {r.osm ?? 0} · Google {r.google ?? 0} · siti letti {r.crawled ?? 0}{r.apify_ig != null ? ` · IG ${r.apify_ig} · TikTok ${r.apify_tiktok}` : ""}{r.google_cost_usd != null ? ` · costo Google $${r.google_cost_usd.toFixed(2)}` : ""}{r.apify_cost_usd != null ? ` · costo Apify $${r.apify_cost_usd.toFixed(3)}` : ""}</div>
    {(r.warnings || []).map((w) => <div key={w} className="text-[#f59e0b]">{w}</div>)}
  </div>
);

const DecisionMakers = ({ list, leadId }) => (list?.length ? (
  <div data-testid={`dm-list-${leadId}`} className="mt-1 flex flex-wrap gap-1.5">
    {list.map((d) => (
      <span key={d.name} className={`text-[10px] font-mono px-1.5 py-0.5 border ${d.verified ? "border-[#10b981]/50 text-[#10b981]" : "border-[#f59e0b]/50 text-[#f59e0b]"}`}>
        {d.name}{d.role && ` · ${d.role}`} · {d.verified ? "verificato" : "da verificare"}
        {d.source_url && <a href={d.source_url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()} className="underline ml-1">fonte</a>}
        {d.linkedin_url && <a href={d.linkedin_url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()} className="underline ml-1">in</a>}
      </span>
    ))}
  </div>
) : null);

const useScrapeJob = (setLeads) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const start = async (payload) => {
    setLoading(true); setReport(null);
    try {
      const { id } = await adminApi.startScrapeJob(payload);
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const j = await adminApi.getScrapeJob(id);
        if (j.status === "error") throw new Error(j.error || "Errore raccolta");
        if (j.status === "done") { setLeads(j.leads); setReport(j.report); toast.success(`${j.leads.length} risultati reali trovati`); return; }
      }
      throw new Error("Tempo scaduto: riprova");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  return { loading, setLoading, report, start };
};

const ImportModal = ({ campaign, day, onClose, onDone }) => {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [area, setArea] = useState(campaign.params?.area || "");
  const [country, setCountry] = useState(CH_RE.test(campaign.params?.area || "") ? "CH" : "");
  const [socials, setSocials] = useState(false);
  const [sources, setSources] = useState({});
  const [importing, setImporting] = useState(false);
  const job = useScrapeJob((l) => { setLeads(l); setSelected([]); });
  useEffect(() => { adminApi.leadSources().then(setSources).catch(() => {}); }, []);

  const scrape = () => job.start({
    professions: [campaign.params?.skill].filter(Boolean), geo_type: "city", geo_cities: [area.trim()].filter(Boolean),
    countries: country ? [country] : ["CH", "IT"], limit: 60, socials, campaign_id: campaign.id,
  });
  const saved = async () => {
    job.setLoading(true);
    try { setLeads((await adminApi.listLeads({ limit: 300 })).leads); setSelected([]); }
    catch (e) { toast.error("Errore caricamento lead"); } finally { job.setLoading(false); }
  };
  const shown = leads.filter((l) => !q || `${l.company_name} ${l.city} ${l.address}`.toLowerCase().includes(q.toLowerCase()));
  const [dmLoading, setDmLoading] = useState(false);
  const findDm = async () => {
    setDmLoading(true);
    try {
      const { results } = await adminApi.findDecisionMakers(selected.slice(0, 10));
      const by = Object.fromEntries(results.map((r) => [r.id, r]));
      setLeads((p) => p.map((l) => (by[l.id] && !by[l.id].error ? { ...l, decision_makers: by[l.id].decision_makers } : l)));
      const n = results.reduce((a, r) => a + r.decision_makers.length, 0);
      toast.success(`${n} responsabili trovati su ${results.length} aziende`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore ricerca responsabili"); }
    finally { setDmLoading(false); }
  };
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const doImport = async () => {
    setImporting(true);
    try {
      const r = await adminApi.importLeads(campaign.id, day, selected);
      toast.success(`Importati ${r.added.contacts} contatti, ${r.added.ig} IG, ${r.added.tiktok} TikTok${r.skipped ? ` · ${r.skipped} già presenti` : ""}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore import"); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div data-testid="import-modal" className="bg-white border border-[#d5dbde] max-w-4xl w-full flex flex-col max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#d5dbde] flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl">Raccogli contatti reali · giorno {day}</h3>
            <p className="text-xs text-[#6b7280] font-mono mt-0.5">Skill: {campaign.params?.skill}</p>
          </div>
          <button data-testid="import-modal-close" onClick={onClose} className="text-[#6b7280] hover:text-[#E0262C]"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-4 flex flex-wrap items-center gap-2 border-b border-[#d5dbde]">
          <Input data-testid="scrape-area" value={area} onChange={(e) => setArea(e.target.value)} placeholder="Zona (es. Paradiso, Lugano)"
            className="h-10 w-56 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-sm" />
          <select data-testid="scrape-country" value={country} onChange={(e) => setCountry(e.target.value)} className={`${selectCls} h-10 text-sm`}>
            <option value="">CH + IT</option><option value="CH">Svizzera</option><option value="IT">Italia</option>
          </select>
          <label className={`flex items-center gap-2 text-xs ${sources.apify_api_token ? "text-[#2B3036]" : "text-[#52525b]"}`} title={sources.apify_api_token ? "" : "Inserisci il token Apify nel tab API Keys"}>
            <Checkbox data-testid="scrape-socials" checked={socials} disabled={!sources.apify_api_token} onCheckedChange={(v) => setSocials(!!v)} />
            Cerca anche profili IG/TikTok (Apify){!sources.apify_api_token && " · token mancante"}
          </label>
          <button data-testid="import-scrape-btn" onClick={scrape} disabled={job.loading || !area.trim()}
            className="aurora-gradient text-white text-sm px-4 h-10 inline-flex items-center gap-2 disabled:opacity-50">
            {job.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Avvia raccolta
          </button>
          <button data-testid="import-saved-btn" onClick={saved} disabled={job.loading}
            className="border border-[#d5dbde] hover:border-[#E0262C] text-[#2B3036] text-sm px-4 h-10">Lead salvati</button>
          <Input data-testid="import-filter" placeholder="Filtra..." value={q} onChange={(e) => setQ(e.target.value)}
            className="h-10 w-40 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-sm ml-auto" />
        </div>
        {job.report && <ScrapeReport r={job.report} />}
        <div className="flex-1 overflow-y-auto">
          {job.loading && <div className="p-10 text-center text-xs font-mono text-[#5b6168]"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#E0262C] mb-3" />Raccolta in corso: cerco le attività e leggo i loro siti (fino a 1-2 minuti)…</div>}
          {!job.loading && shown.map((l) => (
            <label key={l.id} data-testid={`import-lead-${l.id}`} className="flex items-center gap-3 px-6 py-2.5 border-b border-[#e9edef] hover:bg-[#f4f6f7] cursor-pointer">
              <Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggle(l.id)} />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{l.company_name} <span className="text-[9px] font-mono uppercase text-[#6b7280] border border-[#d5dbde] px-1 ml-1">{SRC[l.source] || l.source}</span>
                  {l.followers != null && <span className="text-[10px] font-mono text-[#5b6168] ml-2">{l.followers} follower</span>}</div>
                <div className="text-[11px] font-mono text-[#6b7280] truncate">{[l.address || l.city, l.email, l.website, l.phone].filter(Boolean).join(" · ") || l.bio || "—"}</div>
                <DecisionMakers list={l.decision_makers} leadId={l.id} />
              </div>
              {l.instagram_url && <Instagram className="w-3.5 h-3.5 text-[#E64FA7]" />}
              {l.tiktok_url && <Music className="w-3.5 h-3.5 text-[#2B3036]" />}
            </label>
          ))}
          {!job.loading && !shown.length && <div className="p-10 text-center text-xs font-mono text-[#6b7280]">Avvia la raccolta reale o carica i lead salvati</div>}
        </div>
        <div className="px-6 py-4 border-t border-[#d5dbde] flex items-center justify-between">
          <button data-testid="import-select-all" onClick={() => setSelected(shown.map((l) => l.id))} className="text-xs text-[#5b6168] hover:text-[#E0262C] font-mono">
            Seleziona tutti ({shown.length})
          </button>
          <span className="text-[10px] font-mono text-[#52525b]">Dati © OpenStreetMap contributors (ODbL)</span>
          <button data-testid="find-decision-makers-btn" onClick={findDm} disabled={!selected.length || dmLoading || !sources.perplexity_api_key}
            title={sources.perplexity_api_key ? "Max 10 aziende per volta" : "Inserisci la chiave Perplexity nel tab API Keys"}
            className="border border-[#d5dbde] hover:border-[#E0262C] text-[#2B3036] text-sm px-4 h-10 inline-flex items-center gap-2 disabled:opacity-40">
            {dmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserSearch className="w-4 h-4" />} Trova responsabili ({Math.min(selected.length, 10)})
          </button>
          <button data-testid="import-confirm" onClick={doImport} disabled={!selected.length || importing}
            className="aurora-gradient text-white text-sm px-5 h-10 inline-flex items-center gap-2 disabled:opacity-40">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Importa {selected.length}
          </button>
        </div>
      </div>
    </div>
  );
};

const EMPTY = { type: "contacts", name: "", address: "", email: "", website: "", phone: "", handle: "", url: "" };

const ManualForm = ({ campaign, day, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = (k, ph) => (
    <Input data-testid={`manual-${k}`} placeholder={ph} value={f[k]} onChange={set(k)}
      className="h-9 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-xs" />
  );
  const save = async () => {
    setSaving(true);
    try { await adminApi.addItem(campaign.id, day, f); toast.success("Elemento aggiunto"); setF({ ...EMPTY, type: f.type }); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid={`manual-form-${day}`} className="flex flex-wrap items-center gap-2">
      <select data-testid="manual-type" value={f.type} onChange={set("type")} className={`${selectCls} h-9 text-xs`}>
        <option value="contacts">Contatto</option>
        <option value="ig">Instagram</option>
        <option value="tiktok">TikTok</option>
      </select>
      {f.type === "contacts" ? (
        <>{inp("name", "Ragione sociale / Nome Cognome")}{inp("address", "Indirizzo")}{inp("email", "Email")}{inp("website", "Sito web")}{inp("phone", "Telefono")}</>
      ) : (
        <>{inp("handle", "@account")}{inp("url", "URL profilo")}{inp("name", "Nome (opz.)")}</>
      )}
      <button data-testid="manual-add-btn" onClick={save} disabled={saving}
        className="h-9 px-3 border border-[#d5dbde] hover:border-[#E0262C] text-xs text-[#2B3036] inline-flex items-center gap-1.5">
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Aggiungi
      </button>
    </div>
  );
};

const ItemList = ({ title, items, kind, render, onDelete, locked }) => (
  <div className="border border-[#d5dbde] min-w-0">
    <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-mono text-[#6b7280] border-b border-[#d5dbde]">{title} ({items.length})</div>
    <div className="max-h-60 overflow-y-auto">
      {items.map((i) => (
        <div key={i.id} className="px-3 py-2 border-b border-[#e9edef] flex items-start gap-2 text-xs">
          <div className="min-w-0 flex-1">{render(i)}</div>
          {!locked && (
            <button data-testid={`delete-${kind}-${i.id}`} onClick={() => onDelete(kind, i.id)} className="text-[#52525b] hover:text-[#ef4444]">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      {!items.length && <div className="px-3 py-6 text-center text-[11px] font-mono text-[#52525b]">Vuoto</div>}
    </div>
  </div>
);

export const DayItemsEditor = ({ campaign, row, onChanged }) => {
  const [importOpen, setImportOpen] = useState(false);
  const locked = !row.editable;
  const del = async (kind, itemId) => {
    try { await adminApi.deleteItem(campaign.id, row.day, kind, itemId); toast.success("Eliminato"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const social = (i) => (
    <><a href={i.url} target="_blank" rel="noreferrer" className="text-[#2B3036] hover:text-[#E0262C]">{i.handle}</a>
      {i.name && <div className="text-[#6b7280] truncate">{i.name}</div>}</>
  );
  return (
    <div data-testid={`day-editor-${row.day}`} className="p-4 space-y-4">
      {!locked && (
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid={`import-open-${row.day}`} onClick={() => setImportOpen(true)}
            className="h-9 px-4 aurora-gradient text-white text-xs inline-flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Raccogli contatti reali</button>
          <ManualForm campaign={campaign} day={row.day} onDone={onChanged} />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ItemList title="Contatti" kind="contacts" items={row.contacts} onDelete={del} locked={locked} render={(i) => (
          <><div className="text-[#2B3036] truncate">{i.name}</div>
            <div className="text-[#6b7280] truncate">{i.address}</div>
            <div className="text-[#6b7280] font-mono truncate">{i.email || i.website} · {i.phone}</div></>
        )} />
        <ItemList title="Instagram" kind="ig" items={row.ig} onDelete={del} locked={locked} render={social} />
        <ItemList title="TikTok" kind="tiktok" items={row.tiktok} onDelete={del} locked={locked} render={social} />
      </div>
      {importOpen && <ImportModal campaign={campaign} day={row.day} onClose={() => setImportOpen(false)} onDone={onChanged} />}
    </div>
  );
};
